import type { DocumentUpload } from "@/models"
import { useIncidentHandler } from "@/pages/shared/incident-form/IncidentLogHandler"
import { css } from "vite-css-in-js"
import { computed } from "vue"
import { defineComponent, inferProps, type ReactiveComponent } from "vue-utils"
import ExistingDocumentsRow from "./ExistingDocumentsRow"

interface Props {
	documents: DocumentUpload[]
}

const containerStyles = css`
	flex-shrink: 1;
	overflow: auto;

	margin-top: 0.5rem;
`

const tableStyles = css`
	table-layout: fixed;
	border-collapse: collapse;

	display: grid;

	--color-border: rgba(0, 0, 0, 0.2);

	border-left: thin solid var(--color-border);

	thead,
	tbody,
	tr {
		display: contents;
	}

	th {
		line-height: 1rem;
	}
	td,
	th {
		border-right: thin solid var(--color-border);
		border-bottom: thin solid var(--color-border);
		text-align: center;
		padding: 0.85rem 0.5rem;

		display: flex;
		align-items: center;
		justify-content: center;
		background-color: white;
	}

	tr > *:first-child {
		justify-content: start;
		text-align: left;
	}

	td.actions,
	th.actions {
		border: none;
		background-color: transparent;
	}

	td.actions {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: start;
		gap: 0.25rem;
		padding: 0.25rem;

		button {
			padding: 0.375rem 0.5rem;
		}
	}
`

const ExistingDocuments: ReactiveComponent<Props> = (props) => {
	const handler = useIncidentHandler()

	const sortedDocs = computed(() =>
		[...props.documents].sort((d1, d2) => d1.uploadedBy.timeStamp.compareTo(d2.uploadedBy.timeStamp))
	)

	return () =>
		props.documents.length === 0 ? (
			<p class="m-0">No documents have been uploaded.</p>
		) : (
			<div class={containerStyles}>
				<table
					class={tableStyles}
					style={{
						gridTemplateColumns:
							`minmax(1em, 1fr) minmax(8rem, max-content) auto max-content${ 
							!handler.isNewIncident ? " max-content" : ""}`,
					}}
				>
					<thead>
						<tr>
							<th>File Name</th>
							<th>{handler.isNewIncident ? "Modified" : "Uploaded"}</th>
							<th>Uploaded By</th>
							<th>Size</th>
							{!handler.isNewIncident && <th class="actions"></th>}
						</tr>
					</thead>
					<tbody>
						{sortedDocs.value.map((document) => (
							<ExistingDocumentsRow key={document.guid} document={document} />
						))}
					</tbody>
				</table>
			</div>
		)
}

export default defineComponent(ExistingDocuments, inferProps<Props>())
